import { PlusOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { DownArrow } from "../../../assets/icons";
import { useSelectContext } from "../../../hooks/useSelectContext";
import { FormOption, FormSelect } from "../../atoms/select";
import { StyledSelectAntDiv } from "../../atoms/select/select.styles";
import { MODULES_SELECTION_DATA_ENDPOINTS } from "./utils";

const findSelectOption = (options, selected) =>
  options.find(
    option => option?.value === selected || String(option?.value) === String(selected),
  );

export const resolveSearchableSelectDisplayValue = ({
  value,
  options,
  mode,
  isOptionsPending,
}) => {
  if (mode === "multiple") {
    const selected = Array.isArray(value) ? value : [];
    if (selected.length === 0) return [];
    const matched = selected.map(item => findSelectOption(options, item));
    if (matched.every(Boolean)) return matched.map(option => option.value);
    return isOptionsPending ? [] : value;
  }

  if (value == null || value === "") return null;
  const match = findSelectOption(options, value);
  if (match) return match.value;
  return isOptionsPending ? null : value || null;
};

export const GenericFormSelectAPI = ({
  moduleName,
  placeholder,
  activeRecords,
  params,
  skip = false,
  ...props
}) => {
  const config = MODULES_SELECTION_DATA_ENDPOINTS[moduleName];
  if (!config) throw new Error(`Unknown module config: ${moduleName}`);

  const queryParams = params
    ? STRING_PARAMS_MODULE_LIST.includes(moduleName)
      ? params
      : { params }
    : undefined;
  const { data, isError, isSuccess } = config.func(queryParams, { skip });

  const options = useMemo(() => {
    const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    const fieldName = moduleName === "products" ? "number" : "name";

    const activeListRecords = activeRecords ? list.filter(item => item.is_active) : list;

    const normalized = config.prepare
      ? config.prepare(activeListRecords)
      : activeListRecords.map(item => ({ label: item[fieldName], value: item.id }));
    return normalized;
  }, [config, data, moduleName, activeRecords]);

  return (
    <FormSelect {...props}>
      {isError && (
        <FormOption className="error" disabled={true}>
          Failed to load options
        </FormOption>
      )}
      {isSuccess && options.length === 0 ? (
        <FormOption disabled={true}>No Options</FormOption>
      ) : (
        <>
          {placeholder && (
            <FormOption key="noValue" value="">
              {placeholder}
            </FormOption>
          )}
          {options.map(item => (
            <FormOption key={`${moduleName}-${item.value}`} value={item.value}>
              {item.label}
            </FormOption>
          ))}
        </>
      )}
    </FormSelect>
  );
};

export const GenericFormSelectAPIWithCreate = ({
  moduleName,
  placeholder,
  activeRecords,
  linkOption = {},
  params,
  skip = false,
  onChange,
  ...props
}) => {
  const navigate = useNavigate();
  const config = MODULES_SELECTION_DATA_ENDPOINTS[moduleName];

  if (!config) throw new Error(`Unknown module config: ${moduleName}`);

  const queryParams = params
    ? STRING_PARAMS_MODULE_LIST.includes(moduleName)
      ? params
      : { params }
    : undefined;
  const { data, isError, isSuccess } = config.func(queryParams, { skip });
  const { optionName, link } = linkOption;

  const options = useMemo(() => {
    const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    const fieldName = moduleName === "products" ? "number" : "name";

    const activeListRecords = activeRecords ? list.filter(item => item.is_active) : list;

    const normalized = config.prepare
      ? config.prepare(activeListRecords)
      : activeListRecords.map(item => ({ label: item[fieldName], value: item.id }));
    return normalized;
  }, [config, data, moduleName, activeRecords]);

  const handleClickOnLink = e => {
    const { value } = e.target;

    if (value === "link") {
      navigate("/master/transaction_reason/create");
      return;
    }
    onChange(e);
  };

  return (
    <FormSelect {...props} onChange={handleClickOnLink}>
      {isError && (
        <FormOption className="error" disabled={true}>
          Failed to load options
        </FormOption>
      )}
      {isSuccess && options.length === 0 ? (
        <FormOption disabled={true}>No Options</FormOption>
      ) : (
        <>
          {placeholder && (
            <FormOption key="noValue" value="">
              {placeholder}
            </FormOption>
          )}
          {link && (
            <FormOption className="link" key="link" value="link" onClick={handleClickOnLink}>
              {optionName}
            </FormOption>
          )}
          {options.map(item => (
            <FormOption key={`${moduleName}-${item.value}`} value={item.value}>
              {item.label}
            </FormOption>
          ))}
        </>
      )}
    </FormSelect>
  );
};

/**
 * Renders a searchable and filterable Ant Design Select dropdown populated by RTK Query endpoints.
 * Integrates directly with the active Formik context to manage state.
 *
 * @param {object} props - Component props.
 * @param {string} props.moduleName - Key identifying the target module in `MODULES_SELECTION_DATA_ENDPOINTS`.
 * @param {string} [props.placeholder="Please Select"] - Placeholder text displayed when no option is selected.
 * @param {boolean} [props.activeRecords=false] - If true, filters the fetched options to only include those with `is_active: true`.
 * @param {object|string} [props.params] - Query parameters passed to the RTK Query hook. For modules listed in
 *                                         `STRING_PARAMS_MODULE_LIST`, this is passed directly; otherwise it is wrapped in `{ params }`.
 * @param {boolean} [props.skip=false] - If true, skips the API fetch.
 * @param {function} [props.handleOnChange] - Optional secondary callback fired after Formik's state is updated. Receives `(value, option)`.
 * @param {string} [props.context] - The form context namespace used by `useSelectContext` to access Formik state.
 * @param {string} props.name - The name of the field, used as the key in Formik's state.
 * @param {any} [props.value] - The currently selected value(s).
 * @param {"multiple"|"tags"} [props.mode] - The Ant Design Select mode (e.g., 'multiple' for multi-select).
 * @returns {JSX.Element}
 */
export const GenericFormSearchableSelectAPI = ({
  moduleName,
  placeholder,
  activeRecords,
  params,
  skip = false,
  handleOnChange = null,
  context,
  name,
  value,
  mode,
  refetchOnOpen = false,
  onCreate = null,
  createLabel = "Create",
  popupRender: userPopupRender,
  mapOptions = null,
  ...props
}) => {
  const config = MODULES_SELECTION_DATA_ENDPOINTS[moduleName];

  const {
    formik: { setFieldValue },
  } = useSelectContext(context);

  if (!config) throw new Error(`Unknown module config: ${moduleName}`);

  // Use undefined (not null) when there are no filter params. RTK/query fns that
  // default-destructure `{ params } = {}` work with undefined; null throws.
  // Do not always wrap as `{ params }` — many selection queries treat a truthy
  // arg as a raw query string and would produce `?[object Object]`.
  const queryParams = params
    ? STRING_PARAMS_MODULE_LIST.includes(moduleName)
      ? params
      : { params }
    : undefined;
  const { data, currentData, isLoading, isFetching, isError, refetch } = config.func(queryParams, { skip });

  const options = useMemo(() => {
    // Prevent showing stale options from previous params while fetching new ones
    const activeData = isFetching && currentData === undefined ? [] : data;
    const list = Array.isArray(activeData) ? activeData : Array.isArray(activeData?.results) ? activeData.results : [];
    const fieldName = moduleName === "products" ? "number" : "name";

    const activeListRecords = activeRecords ? list.filter(item => item.is_active) : list;

    const normalized = config.prepare
      ? config.prepare(activeListRecords)
      : activeListRecords.map(item => ({ label: item[fieldName], value: item.id }));
    return mapOptions ? mapOptions(normalized) : normalized;
  }, [config, data, currentData, isFetching, mapOptions, moduleName, activeRecords]);

  const handleChange = (value, option) => {
    setFieldValue(name, value);
    if (handleOnChange) {
      handleOnChange(value, option);
    }
  };

  // Ant Design prints the raw value when it is not in `options` yet. A saved
  // id (for example currency 43) would flash before the label ("Euro") loads.
  const displayValue = resolveSearchableSelectDisplayValue({
    value,
    options,
    mode,
    isOptionsPending: (isLoading || isFetching) && options.length === 0,
  });

  const popupRender =
    onCreate || userPopupRender
      ? menu => {
          const body = userPopupRender ? userPopupRender(menu) : menu;
          if (!onCreate) return body;
          return (
            <>
              <div
                role="button"
                tabIndex={0}
                style={{
                  padding: "8px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  color: "#1677ff",
                  borderBottom: "1px solid #e9edf5",
                }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => onCreate()}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onCreate();
                  }
                }}
              >
                <PlusOutlined />
                <span>{createLabel}</span>
              </div>
              {body}
            </>
          );
        }
      : undefined;

  return (
    <StyledSelectAntDiv className={mode === "multiple" ? "multiple" : ""}>
      <Select
        {...props}
        onDropdownVisibleChange={open => {
          if (open && refetchOnOpen && !skip) refetch();
          props.onDropdownVisibleChange?.(open);
        }}
        popupRender={popupRender}
        suffixIcon={<DownArrow />}
        status={isError ? "error" : undefined}
        notFoundContent={isError ? "Failed to load options" : "No Options"}
        showSearch
        allowClear
        loading={isFetching || isLoading}
        style={{ width: "100%", ...props.style }}
        placeholder={placeholder || "Please Select"}
        onChange={handleChange}
        options={options}
        mode={mode}
        filterOption={(input, option) =>
          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
        }
        value={displayValue}
      />
    </StyledSelectAntDiv>
  );
};

const STRING_PARAMS_MODULE_LIST = ["supplier"];
